import * as types from "./types";
import FeenyAPI from "../../services/feeny";
import { normalize, schema } from "../normalize";

const readingSchema = new schema.Entity(
  "readings",
  {},
  { idAttribute: "uuid" }
);

const defaultReadingEntities = {
  readings: {}
};

const assignmentSchema = new schema.Entity(
  "assignments",
  {
    readings: [readingSchema]
  },
  { idAttribute: "uuid" }
);

const defaultAssignmentEntities = {
  assignments: {},
  readings: {}
};

const assignmentListSchema = new schema.Array(assignmentSchema);

const fetchAssignmentsRequest = () => ({
  type: types.FETCH_ASSIGNMENTS_REQUEST
});

const fetchAssignmentsSuccess = data => ({
  type: types.FETCH_ASSIGNMENTS_SUCCESS,
  data
});

export const fetchAssignmentsFailure = error => ({
  type: types.FETCH_ASSIGNMENTS_FAILURE,
  error
});

export const fetchAssignments = meta => async dispatch => {
  dispatch(fetchAssignmentsRequest());
  await FeenyAPI.init();
  return FeenyAPI.Assignments.all()
    .then(({ data }) => {
      dispatch(
        fetchAssignmentsSuccess(
          normalize(data, assignmentListSchema, defaultAssignmentEntities)
        )
      );
    })
    .catch(error => dispatch(fetchAssignmentsFailure(error)));
};

export const fetchAssignmentRequest = assignmentId => ({
  type: types.FETCH_ASSIGNMENT_REQUEST,
  assignmentId
});

export const fetchAssignmentSuccess = (assignmentId, data) => ({
  type: types.FETCH_ASSIGNMENT_SUCCESS,
  assignmentId,
  data
});

const fetchAssignmentFailure = (assignmentId, error) => ({
  type: types.FETCH_ASSIGNMENT_FAILURE,
  assignmentId,
  error
});

export const fetchAssignment = assignmentId => async dispatch => {
  dispatch(fetchAssignmentRequest(assignmentId));
  await FeenyAPI.init();
  return FeenyAPI.Assignments.one(assignmentId)
    .then(({ data }) => {
      dispatch(
        fetchAssignmentSuccess(
          assignmentId,
          normalize(data, assignmentSchema, defaultAssignmentEntities)
        )
      );
      return data;
    })
    .catch(error => dispatch(fetchAssignmentFailure(assignmentId, error)));
};

export const createAssignmentRequest = () => ({
  type: types.CREATE_ASSIGNMENT_REQUEST
});

export const createAssignmentSuccess = data => ({
  type: types.CREATE_ASSIGNMENT_SUCCESS,
  data
});

export const createAssignmentFailure = error => ({
  type: types.CREATE_ASSIGNMENT_FAILURE,
  error
});

/**
 * Creates a new Assignment
 * @param {Object} data - Data to create the assignnment.
 * @param {string} data.title - The title of the assignment.
 * @param {string} data.vbid - The vbid the assignment belongs to.
 */
export const createAssignment = data => async dispatch => {
  dispatch(createAssignmentRequest());
  await FeenyAPI.init();
  return FeenyAPI.Assignments.create(data)
    .then(({ data }) => {
      dispatch(
        createAssignmentSuccess(
          normalize(data, assignmentSchema, defaultAssignmentEntities)
        )
      );
      return data;
    })
    .catch(error => dispatch(createAssignmentFailure(error)));
};

const deleteAssignmentRequest = assignmentId => ({
  type: types.DELETE_ASSIGNMENT_REQUEST,
  assignmentId
});

const deleteAssignmentSuccess = assignmentId => ({
  type: types.DELETE_ASSIGNMENT_SUCCESS,
  assignmentId
});

const deleteAssignmentFailure = (assignmentId, error) => ({
  type: types.DELETE_ASSIGNMENT_FAILURE,
  assignmentId,
  error
});

export const deleteAssignment = assignmentId => async dispatch => {
  dispatch(deleteAssignmentRequest(assignmentId));
  await FeenyAPI.init();
  return FeenyAPI.Assignments.delete(assignmentId)
    .then(() => dispatch(deleteAssignmentSuccess(assignmentId)))
    .catch(error => dispatch(deleteAssignmentFailure(assignmentId, error)));
};

const updateAssignmentRequest = (assignmentId, data) => ({
  type: types.UPDATE_ASSIGNMENT_REQUEST,
  assignmentId,
  data
});

const updateAssignmentSuccess = (assignmentId, data) => ({
  type: types.UPDATE_ASSIGNMENT_SUCCESS,
  assignmentId,
  data
});

const updateAssignmentFailure = (assignmentId, error, oldData) => ({
  type: types.UPDATE_ASSIGNMENT_FAILURE,
  assignmentId,
  data: oldData, // TODO is this normalized?
  error
});

export const updateAssignment = (assignment, data) => async dispatch => {
  const assignmentId = assignment.uuid;
  dispatch(updateAssignmentRequest(assignmentId, data));
  await FeenyAPI.init();
  return FeenyAPI.Assignments.update(assignmentId, data)
    .then(({ data }) => {
      dispatch(
        updateAssignmentSuccess(
          assignmentId,
          normalize(data, assignmentSchema, defaultAssignmentEntities)
        )
      );
      return data;
    })
    .catch(error =>
      dispatch(updateAssignmentFailure(assignment.uuid, error, assignment))
    );
};

const createReadingRequest = () => ({
  type: types.CREATE_READING_REQUEST
});

const createReadingSuccess = (assignmentId, data) => ({
  type: types.CREATE_READING_SUCCESS,
  assignmentId,
  data
});

const createReadingFailure = error => ({
  type: types.CREATE_READING_FAILURE,
  error
});

/**
 * Creates a new reading for an assignment
 * @param {string} assignmentId - The uuid of the assignment
 * @param {Object} data - The reading data
 * @param {string} data.description - Description of the reading.
 * @param {string} data.summary - Summary of the reading. Displayed as the reading preview
 * @param {string} data.start - Start cfi of the reading
 * @param {string} data.end - End cfi of the reading
 * @param {integer} data.order - Order of the reading in the assignment
 * @param {string} data.type - Type of the reading - Book format? "pbk" "epub"
 * @param {boolean} data.exclusive - Is the end cfi inclusive or exclusive
 * @param {boolean} data.range_type - Range type of the reading - "pbk" ? "page_index" : "cfi"
 * @param {Array} meta - array of metadata about the reading [{tooling: "contents-selection", tollhouse: true}]
 */
export const createReading = (assignmentId, data, meta) => async dispatch => {
  dispatch(createReadingRequest());
  await FeenyAPI.init();
  return FeenyAPI.Readings.create(assignmentId, data, meta)
    .then(({ data }) => {
      // TODO do we need to create a UserReading?
      dispatch(
        createReadingSuccess(
          assignmentId,
          normalize(data, readingSchema, defaultReadingEntities)
        )
      );
      return data;
    })
    .catch(error => dispatch(createReadingFailure(error)));
};

const deleteReadingRequest = readingId => ({
  type: types.DELETE_READING_REQUEST,
  readingId
});

const deleteReadingSuccess = (assignmentId, reading) => ({
  type: types.DELETE_READING_SUCCESS,
  assignmentId,
  reading
});

const deleteReadingFailure = (assignmentId, reading, error) => ({
  type: types.DELETE_READING_FAILURE,
  assignmentId,
  reading,
  error
});

export const deleteReading = (assignmentId, reading) => async dispatch => {
  const readingId = reading.uuid;
  dispatch(deleteReadingRequest(readingId));
  await FeenyAPI.init();
  return FeenyAPI.Readings.delete(assignmentId, readingId)
    .then(() => dispatch(deleteReadingSuccess(assignmentId, reading)))
    .catch(error =>
      dispatch(deleteReadingFailure(assignmentId, reading, error))
    );
};

const fetchUserAssignmentsRequest = () => ({
  type: types.FETCH_USER_ASSIGNMENTS_REQUEST
});

const fetchUserAssignmentsSuccess = data => ({
  type: types.FETCH_USER_ASSIGNMENTS_SUCCESS,
  data
});

const fetchUserAssignmentsFailure = error => ({
  type: types.FETCH_USER_ASSIGNMENTS_FAILURE,
  error
});

const shouldFetchUserAssignments = state =>
  !state.feeny.userAssignments.isLoaded &&
  !state.feeny.userAssignments.isFetching;

export const fetchUserAssignments = (meta, force = false) => async (
  dispatch,
  getState
) => {
  const state = getState();

  if (force || shouldFetchUserAssignments(state)) {
    dispatch(fetchUserAssignmentsRequest());
    await FeenyAPI.init();
    return FeenyAPI.UserAssignments.all({ status: "all" })
      .then(({ data }) => {
        dispatch(
          fetchUserAssignmentsSuccess(
            normalize(data, assignmentListSchema, defaultAssignmentEntities)
          )
        );
        return data;
      })
      .catch(error => dispatch(fetchUserAssignmentsFailure(error)));
  }
};

const fetchUserAssignmentRequest = userAssignmentId => ({
  type: types.FETCH_USER_ASSIGNMENT_REQUEST,
  userAssignmentId
});

const fetchUserAssignmentSuccess = (userAssignmentId, data) => ({
  type: types.FETCH_USER_ASSIGNMENT_SUCCESS,
  userAssignmentId,
  data
});

const fetchUserAssignmentFailure = (userAssignmentId, error) => ({
  type: types.FETCH_USER_ASSIGNMENT_FAILURE,
  userAssignmentId,
  error
});

export const fetchUserAssignment = userAssignmentId => async dispatch => {
  dispatch(fetchUserAssignmentRequest(userAssignmentId));
  await FeenyAPI.init();
  return FeenyAPI.UserAssignments.one(userAssignmentId)
    .then(({ data }) => {
      dispatch(
        fetchUserAssignmentSuccess(
          userAssignmentId,
          normalize(data, assignmentSchema, defaultAssignmentEntities)
        )
      );
      return data;
    })
    .catch(error =>
      dispatch(fetchUserAssignmentFailure(userAssignmentId, error))
    );
};

const createUserAssignmentRequest = () => ({
  type: types.CREATE_USER_ASSIGNMENT_REQUEST
});

const createUserAssignmentSuccess = data => ({
  type: types.CREATE_USER_ASSIGNMENT_SUCCESS,
  data
});

const createUserAssignmentFailure = error => ({
  type: types.CREATE_USER_ASSIGNMENT_FAILURE,
  error
});

export const createUserAssignment = assignmentId => async dispatch => {
  dispatch(createUserAssignmentRequest());
  await FeenyAPI.init();
  return FeenyAPI.UserAssignments.create(assignmentId)
    .then(({ data }) => {
      dispatch(
        createUserAssignmentSuccess(
          normalize(data, assignmentSchema, defaultAssignmentEntities)
        )
      );
      return data;
    })
    .catch(error => dispatch(createUserAssignmentFailure(error)));
};

const deleteUserAssignmentRequest = userAssignmentId => ({
  type: types.DELETE_USER_ASSIGNMENT_REQUEST,
  userAssignmentId
});

const deleteUserAssignmentSuccess = userAssignmentId => ({
  type: types.DELETE_USER_ASSIGNMENT_SUCCESS,
  userAssignmentId
});

const deleteUserAssignmentFailure = (userAssignmentId, error) => ({
  type: types.DELETE_USER_ASSIGNMENT_FAILURE,
  userAssignmentId,
  error
});

export const deleteUserAssignment = userAssignmentId => async dispatch => {
  dispatch(deleteUserAssignmentRequest(userAssignmentId));
  await FeenyAPI.init();
  return FeenyAPI.UserAssignments.delete(userAssignmentId)
    .then(() => dispatch(deleteUserAssignmentSuccess(userAssignmentId)))
    .catch(error =>
      dispatch(deleteUserAssignmentFailure(userAssignmentId, error))
    );
};

// TODO
// We'll want to not re-fetch decks/cards if they're already loaded or currently loading

export const internal = {
  fetchAssignmentsRequest,
  fetchAssignmentsSuccess,
  fetchAssignmentsFailure,
  createAssignmentRequest,
  createAssignmentSuccess,
  createAssignmentFailure,
  deleteAssignmentRequest,
  deleteAssignmentSuccess,
  deleteAssignmentFailure
};
